import { useState, useEffect, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@/shared/ui/mui';
import { viewportManager } from '../../app/utils/viewport';

export function useResponsive() {
  const theme = useTheme();

  // Orientation detection
  const isPortraitMedia = useMediaQuery('(orientation: portrait)');
  const isLandscapeMedia = useMediaQuery('(orientation: landscape)');

  // Track orientation changes for Android fix
  const [orientationChangeCount, setOrientationChangeCount] = useState(0);

  // Listen for orientation changes to force re-render on Android
  useEffect(() => {
    const cleanup = viewportManager.addOrientationChangeListener(() => {
      setOrientationChangeCount(prev => prev + 1);
    });

    return cleanup;
  }, []);

  // Breakpoint checks (must be called at top level per hooks rules)
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const isSmScreen = useMediaQuery(theme.breakpoints.only('sm'));
  const isMdScreen = useMediaQuery(theme.breakpoints.only('md'));
  const isLgScreen = useMediaQuery(theme.breakpoints.only('lg'));
  const isXlScreen = useMediaQuery(theme.breakpoints.only('xl'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return useMemo(() => ({
    theme,

    // Specific breakpoint checks
    isXsScreen,
    isSmScreen,
    isMdScreen,
    isLgScreen,
    isXlScreen,

    // Range checks
    isMobile,
    isTablet,
    isDesktop,

    // Custom range helpers (aliases)
    isSmallScreen: isMobile,
    isMediumScreen: isTablet,
    isLargeScreen: isDesktop,

    // Orientation detection (fixed for Android)
    isPortrait: isPortraitMedia,
    isLandscape: isLandscapeMedia,
    orientation: isPortraitMedia ? 'portrait' as const : 'landscape' as const,

    // Internal counter to force re-render on Android orientation change
    _orientationVersion: orientationChangeCount,
  }), [
    theme,
    isXsScreen, isSmScreen, isMdScreen, isLgScreen, isXlScreen,
    isMobile, isTablet, isDesktop,
    isPortraitMedia, isLandscapeMedia,
    orientationChangeCount,
  ]);
}
